/**
 * OneShotController - manages temporary one-shot animation playback.
 * Suppresses base/layer state machine output, plays a single clip to completion,
 * then restores the previous state or freezes on the last frame.
 */
import * as THREE from 'three';

import type { AnimationGraphRuntime, AnimationState } from './AnimationTypes.js';

export interface OneShotHost {
  getMixer(): THREE.AnimationMixer | null;
  getAction(name: string): THREE.AnimationAction | undefined;
  getActions(): Map<string, THREE.AnimationAction>;
  getOneShotClips(): Set<string>;
  getBaseCurrentState(): string | null;
  setBaseCurrentState(state: string | null): void;
  getBaseStates(): Record<string, AnimationState>;
  getBaseGraphRuntime(): AnimationGraphRuntime | null;
  enabled: boolean;
  getLayerActions(): Map<string, THREE.AnimationAction>;
  getMaskedActions(): Map<string, THREE.AnimationAction>;
  getLayerRuntime(): AnimationGraphRuntime | null;
  applyGraphStateActions(runtime: AnimationGraphRuntime): void;
  applyOverrideBlend(): void;
  play(clip: string, blendDuration: number): THREE.AnimationAction | null;
  resolveClipFromState(clip: string | string[]): string | null;
  applyBaseBlendWeights(blend: Array<{ clip: string; weight?: number; weightParam?: string; reversed?: boolean }>): boolean;
  getPreferredWeightedAction(actions: Array<THREE.AnimationAction | undefined>): THREE.AnimationAction | undefined;
  getActiveActions(): THREE.AnimationAction[];
  resetActionTime(action: THREE.AnimationAction): void;
}

export class OneShotController {
  private isPlayingFlag = false;
  private prevState: string | null = null;
  private resolve: (() => void) | null = null;
  private currentClip: string | null = null;
  private freezeOnLastFrame = false;
  private layersSuppressed = false;
  private suspendedActionWeights = new Map<string, number>();
  private suspendedActionEnabled = new Map<string, boolean>();
  private suspendedLayerEnabled: boolean | null = null;
  private suspendedLayerWeight: number | null = null;

  constructor(private readonly host: OneShotHost) {}

  get isPlaying(): boolean {
    return this.isPlayingFlag;
  }

  get currentClipName(): string | null {
    return this.currentClip;
  }

  /**
   * Trigger a one-shot animation by name. Returns a promise that resolves when the one-shot finishes.
   */
  playOneShot(clip: string, freezeOnLastFrame: boolean = false): Promise<void> {
    if (!this.host.getOneShotClips().has(clip)) {
      throw new Error(`Clip '${clip}' is not a registered one-shot clip.`);
    }

    if (this.isPlayingFlag) {
      this.interrupt();
    }

    const mixer = this.host.getMixer();
    if (!mixer) return Promise.resolve();

    const action = this.host.getAction(clip);
    if (!action) {
      throw new Error(`One-shot animation action '${clip}' not found.`);
    }

    this.isPlayingFlag = true;
    this.prevState = this.host.getBaseCurrentState();
    this.currentClip = clip;
    this.freezeOnLastFrame = freezeOnLastFrame;

    this.suppressLayers();

    for (const [, act] of this.host.getActions().entries()) {
      if (act !== action) {
        act.fadeOut(0.2);
      }
    }
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.enabled = true;
    action.setEffectiveWeight(1);
    action.play();
    this.host.resetActionTime(action);

    return new Promise<void>((resolveCallback) => {
      this.resolve = resolveCallback;
      action.getMixer().addEventListener('finished', this._onFinished);
    });
  }

  /**
   * Interrupts the currently playing one-shot and cleans up state.
   */
  interrupt(): void {
    if (!this.isPlayingFlag || !this.currentClip) return;

    const currentAction = this.host.getAction(this.currentClip);
    const mixer = this.host.getMixer();
    if (currentAction && mixer) {
      currentAction.getMixer().removeEventListener('finished', this._onFinished);
      currentAction.stop();
      currentAction.enabled = false;
      currentAction.setEffectiveWeight(0);
    }

    if (this.resolve) {
      this.resolve();
      this.resolve = null;
    }

    this.resetState();
    this.restoreLayers();
  }

  /**
   * Clear one-shot state without restoring layers (e.g. when clearing all animations).
   */
  reset(): void {
    if (this.isPlayingFlag && this.currentClip) {
      const currentAction = this.host.getAction(this.currentClip);
      const mixer = this.host.getMixer();
      if (currentAction && mixer) {
        currentAction.getMixer().removeEventListener('finished', this._onFinished);
      }
    }
    this.isPlayingFlag = false;
    this.currentClip = null;
    this.prevState = null;
    this.freezeOnLastFrame = false;
    this.layersSuppressed = false;
    this.suspendedActionWeights.clear();
    this.suspendedActionEnabled.clear();
    this.suspendedLayerEnabled = null;
    this.suspendedLayerWeight = null;
    if (this.resolve) {
      this.resolve();
      this.resolve = null;
    }
  }

  private resetState(): void {
    this.isPlayingFlag = false;
    this.currentClip = null;
    this.freezeOnLastFrame = false;
    this.prevState = null;
  }

  private _onFinished = (event: { action?: THREE.AnimationAction }): void => {
    if (!this.isPlayingFlag) return;
    if (!event?.action) return;
    if (event.action.getClip().name !== this.currentClip) return;

    event.action.getMixer().removeEventListener('finished', this._onFinished);

    if (this.freezeOnLastFrame) {
      this.host.enabled = false;
      this.restoreLayers();
    } else {
      const oneShotAction = event.action;
      const prevState = this.prevState;

      if (prevState) {
        this.host.setBaseCurrentState(prevState);
        const resumedState = this.host.getBaseStates()[prevState];
        if (resumedState) {
          if (resumedState.blend && resumedState.blend.length > 0) {
            const baseRuntime = this.host.getBaseGraphRuntime();
            if (baseRuntime) {
              this.host.applyGraphStateActions(baseRuntime);
            }
            const nextAction = this.host.getPreferredWeightedAction(this.host.getActiveActions());
            if (nextAction) {
              oneShotAction.crossFadeTo(nextAction, 0.3, false);
            } else {
              oneShotAction.setEffectiveWeight(1);
            }
            this.resetState();
            this.restoreLayers();
          } else if (resumedState.clip) {
            const clipToPlay = this.host.resolveClipFromState(resumedState.clip);
            if (clipToPlay) {
              this.host.play(clipToPlay, 0.3);
              this.resetState();
              this.restoreLayers();
            }
          }
        }
      } else {
        this.resetState();
        oneShotAction.stop();
        oneShotAction.enabled = false;
        oneShotAction.setEffectiveWeight(0);
        this.restoreLayers();
      }
    }

    if (this.resolve) {
      this.resolve();
      this.resolve = null;
    }
  };

  private suppressLayers(): void {
    if (this.layersSuppressed) return;

    this.suspendedActionWeights.clear();
    this.suspendedActionEnabled.clear();
    const layerRuntime = this.host.getLayerRuntime();
    this.suspendedLayerEnabled = layerRuntime?.config.enabled ?? null;
    this.suspendedLayerWeight = layerRuntime?.config.weight ?? null;

    for (const [actionName, action] of this.host.getLayerActions().entries()) {
      this.suspendedActionWeights.set(actionName, action.getEffectiveWeight());
      this.suspendedActionEnabled.set(actionName, action.enabled);
      action.setEffectiveWeight(0);
      action.enabled = false;
    }

    for (const maskedAction of this.host.getMaskedActions().values()) {
      maskedAction.setEffectiveWeight(0);
      maskedAction.enabled = false;
    }

    this.layersSuppressed = true;
  }

  private restoreLayers(): void {
    if (!this.layersSuppressed) return;

    for (const [actionName, action] of this.host.getLayerActions().entries()) {
      const wasEnabled = this.suspendedActionEnabled.get(actionName) ?? true;
      const previousWeight = this.suspendedActionWeights.get(actionName) ?? 0;
      action.enabled = wasEnabled;
      action.setEffectiveWeight(previousWeight);
      if (wasEnabled && !action.isRunning()) {
        action.play();
      }
    }

    const layerRuntime = this.host.getLayerRuntime();
    if (layerRuntime) {
      if (this.suspendedLayerEnabled !== null) {
        layerRuntime.config.enabled = this.suspendedLayerEnabled;
      }
      if (this.suspendedLayerWeight !== null) {
        layerRuntime.config.weight = this.suspendedLayerWeight;
      }
    }

    for (const maskedAction of this.host.getMaskedActions().values()) {
      maskedAction.enabled = true;
    }

    this.suspendedActionEnabled.clear();
    this.suspendedActionWeights.clear();
    this.suspendedLayerEnabled = null;
    this.suspendedLayerWeight = null;
    this.layersSuppressed = false;
    if (layerRuntime) {
      this.host.applyGraphStateActions(layerRuntime);
    }
    this.host.applyOverrideBlend();
  }
}
